import { Bell } from "lucide-react";
import { Link } from "react-router-dom";
import { DollarAvatar, useDollar } from "@/lib/dollar";
import type { BudgetResponse } from "@/lib/budget";
import type { PlanTotals } from "@/lib/useBudgetLines";
import { formatDateTime, money } from "@/lib/format";

export function ToAssignCard({ t }: { t: PlanTotals }) {
  const over = t.unassigned < -0.005;
  return (
    <div className="rounded-[14px] bg-[#1B2A4A] p-6 text-white" data-testid="to-assign-card">
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-500">Queda por asignar</p>
      <p className={`tabular mt-3 font-display text-4xl font-extrabold ${over ? "text-[#F3B5A6]" : ""}`} data-testid="to-assign-amount">{money(t.unassigned)}</p>
      <p className="tabular mt-2 text-sm text-white/70" data-testid="to-assign-text">
        {over ? `Estás repartiendo ${money(-t.unassigned)} más de lo que ganas.` : `Ingresos ${money(t.income)} − ahorro ${money(t.savings)} − gastos ${money(t.essentials + t.comfort)} − deudas ${money(t.debts)}.`}
      </p>
    </div>
  );
}

export function RemainingCard({ data }: { data: BudgetResponse }) {
  return (
    <div className="rounded-[14px] bg-[#1B2A4A] p-6 text-white" data-testid="remaining-card">
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-500">Te queda por gastar</p>
      <p className={`tabular mt-3 font-display text-4xl font-extrabold ${data.remaining_to_spend < 0 ? "text-[#F3B5A6]" : ""}`} data-testid="remaining-to-spend">{money(data.remaining_to_spend)}</p>
      <p className="tabular mt-2 text-sm text-white/70">Planeaste {money(data.planned.total)} y van {money(data.actual.total)}.</p>
    </div>
  );
}

export function UnassignedCard({ data }: { data: BudgetResponse }) {
  const over = data.is_overplanned;
  return (
    <div className={`rounded-[14px] border p-6 ${over ? "border-[#B23A1E]/40 bg-[#F8ECE8]" : "border-[#DDE1E8] bg-white"}`} data-testid="unassigned-card">
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#1B2A4A]">Sin destino</p>
      <p className={`tabular mt-3 font-display text-3xl font-extrabold ${over ? "text-[#B23A1E]" : "text-[#1B2A4A]"}`} data-testid="unassigned-amount">{money(data.planned.unassigned)}</p>
      <p className="mt-2 text-sm text-slate-500" data-testid="unassigned-text">
        {data.income_base <= 0 ? "Captura tu ingreso mensual para calcularlo."
          : over ? "Tu plan está sobregirado: planeaste más de lo que ganas."
          : data.planned.unassigned > 0 ? "Dinero de tu ingreso que todavía no tiene plan." : "Cada peso de tu ingreso tiene un destino."}
      </p>
    </div>
  );
}

export function AlertsCard({ data }: { data: BudgetResponse }) {
  const alerts = data.alerts.filter((a) => a.alert_type !== "welcome");
  return (
    <div className="rounded-[14px] border border-[#DDE1E8] bg-white p-6" data-testid="alerts-card">
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-[#1B2A4A]"><Bell className="h-3.5 w-3.5" /> Avisos del mes</p>
      {alerts.length === 0 ? <p className="mt-3 text-sm text-slate-500">Sin avisos por ahora.</p> : (
        <ul className="mt-3 space-y-3">
          {alerts.map((a) => (
            <li key={a.id} className="border-l-2 border-[#F59E0B] pl-3" data-testid={`alert-${a.alert_type}`}>
              <p className="text-sm font-semibold text-[#1B2A4A]">{a.title}</p>
              <p className="text-xs text-slate-500">{a.body} · {formatDateTime(a.created_at)}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function MentorTeaser() {
  const { state } = useDollar();
  return (
    <Link to="/mentor" className="flex items-center gap-4 rounded-[14px] border border-[#E7DECF] bg-white p-5 transition-colors hover:bg-[#F5F0E6]" data-testid="mentor-teaser">
      <DollarAvatar stage={state?.stage ?? "puppy"} className="h-14 w-14" />
      <span className="text-sm font-semibold text-[#16294D]">¿Dudas con tu presupuesto? <span className="text-[#2C4F91] underline-offset-2 hover:underline">Pregúntale a Dollar</span></span>
    </Link>
  );
}
